import { Component, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OccupationLabelPipe } from './pipes/occupation-label.pipe';
import { OccupationPreviewComponent } from './components/occupation-preview/occupation-preview.component';
import { ActivatedRoute, Router } from '@angular/router';
import { occupationsToFormValue, RoadOccupationForm, RoadOccupationFormValue } from './forms/road-occupation.form';
import { RoadOccupationFormComponent } from './components/road-occupation-form/road-occupation-form.component';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { ApplicationAppEntityApplicationRead, RoadOccupationAppEntityApplicationRead } from '../../../../../openapi';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { loadRoadOccupationTypes, updateOccupations } from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';

export interface OccupationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addDecisionControls?: boolean;
  hideOccupationTypeControl?: boolean;
  hidePurposeControl?: boolean;
  addWorkPurposeControl?: boolean;
  workPurposeControlType?: 'select' | 'textarea';
  addSurfaceViolationControl?: boolean;
  addDeviceTypeControl?: boolean;
  addAgreementControls?: boolean;
}

@Component({
  selector: 'app-occupation-data-section',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    SpinnerComponent,
    AccordionGroupComponent,
    AccordionItemComponent,
    OccupationLabelPipe,
    OccupationPreviewComponent,
    RoadOccupationFormComponent,
    ReactiveFormsModule,
    StagesComponent
  ],
  providers: [RoadOccupationForm],
  templateUrl: './occupation-data-section.component.html'
})
export class OccupationDataSectionComponent implements OnInit, OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public workPurposeControlType: 'select' | 'textarea' = this.route.snapshot.data['workPurposeControlType'] || 'select';

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public showItemForm: boolean = false;

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public roadOccupations: WritableSignal<RoadOccupationFormValue[]> = signal([]);

  private fetchRoadOccupations: Subscription = toObservable(this.application).subscribe(app => {
    const occupations: Array<RoadOccupationAppEntityApplicationRead> = app?.roadOccupations || [];
    this.roadOccupations.set(occupationsToFormValue(occupations));
  });

  constructor(
    public form: RoadOccupationForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onAdd(): void {
    this.showItemForm = true;
    this.currentIndex.set(undefined);
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public onEditOccupations(event: Event, i: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentIndex.set(i);
    this.showItemForm = true;
    this.form.patchFormValues({
      ...this.roadOccupations()[i]
    });
  }

  public onUpdateOccupations(roadOccupation: RoadOccupationFormValue): void {
    if (this.currentIndex() !== undefined) {
      this.roadOccupations.update(v => {
        const arr = [...v];
        arr[this.currentIndex() || 0] = roadOccupation;
        return arr;
      });
      this.currentIndex.set(undefined);
    } else {
      this.roadOccupations.update(v => ([
        ...v,
        roadOccupation
      ]));
    }
    this.showItemForm = false;
    this.form.reset();
  }

  public onRemove(i: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.roadOccupations.update(v => {
      v.splice(i, 1);
      return v;
    });
  }

  public submitAndGoNext(): void {
    if (this.roadOccupations().length) {
      this.store$.dispatch(updateOccupations({
        application: this.application(),
        occupations: this.roadOccupations(),
        successFnc: (applicationId) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadRoadOccupationTypes());
    if (this.route.snapshot.data['addDecisionControls']) {
      this.form.createDecisionControls();
    }
    if (this.route.snapshot.data['hideOccupationTypeControl']) {
      this.form.removeControl('occupationType');
    }
    if (this.route.snapshot.data['hidePurposeControl']) {
      this.form.removeControl('purpose');
    }
    if (this.route.snapshot.data['addSurfaceViolationControl']) {
      this.form.createSurfaceViolationControl();
    }
    if (this.route.snapshot.data['addWorkPurposeControl']) {
      this.form.createWorkPurposeControl();
    }
    if (this.route.snapshot.data['addDeviceTypeControl']) {
      this.form.createDeviceTypeControl();
    }
    if (this.route.snapshot.data['addAgreementControls']) {
      this.form.createAgreementControls();
    }
  }

  public ngOnDestroy(): void {
    this.fetchRoadOccupations.unsubscribe();
  }

}
